// ts-rest contract for View > Client Intelligence — the Attrition Risk
// read-model projection.
//
// The attrition score is a DERIVED read-model: the server computes it live
// from ontology data (PortfolioPerformanceSnapshot, DeskCall, ClientReview,
// SuitabilityAlert, ConsumerDutyClientScore, Fee, VulnerabilityFlag, Client)
// per active client and maps it to the per-view shape below. There is no
// stored Attrition object / table — the controller is a pure projection
// (CLAUDE.md "Developing a view"). AUM and annual revenue are display-only
// (revenue-blind score) and never feed the band.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// Band after the hard-trip floors are applied. `Insufficient data` is a
// distinct outcome when signal coverage is below the policy floor — it does
// NOT fall through to Low.
export const attritionBandSchema = z.enum(['High', 'Medium', 'Low', 'Insufficient data']);
export type AttritionBand = z.infer<typeof attritionBandSchema>;

// One active client's attrition projection. `owner` (IM) is deliberately
// absent: it is not derivable until a ClientAssignment Object Type exists
// (no fabrication — see CLAUDE.md gaps note).
export const attritionRowSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    band: attritionBandSchema,
    // 0–100 composite (re-normalised across assessable signals), surfaced for
    // ordering / debugging. `null` when the band is `Insufficient data`.
    score: z.number().nullable(),
    // Fraction of total signal weight that was assessable, [0,1].
    coverage: z.number(),
    // Display-only AUM (latest closing portfolio value, GBP). Never a score input.
    aum: z.number(),
    // Display-only trailing-12m revenue (sum of management/adviser fees, GBP).
    annualRevenue: z.number(),
    // Itemised top contributing signals as short human-readable strings.
    factors: z.array(z.string()),
});
export type AttritionRow = z.infer<typeof attritionRowSchema>;

// Per-band client counts (the four bands the score can produce).
export const attritionBandCountsSchema = z.object({
    high: z.number(),
    medium: z.number(),
    low: z.number(),
    insufficientData: z.number(),
});
export type AttritionBandCounts = z.infer<typeof attritionBandCountsSchema>;

// The derivable KPI summary. `Client Retention %` is intentionally absent —
// it depends on off-board events that are not yet ingested (no fabrication).
export const attritionKpiSummarySchema = z.object({
    totalClients: z.number(),
    // High + Medium count.
    atRiskCount: z.number(),
    // Sum of display-only annual revenue over High + Medium clients (GBP).
    atRiskRevenue: z.number(),
    bandCounts: attritionBandCountsSchema,
});
export type AttritionKpiSummary = z.infer<typeof attritionKpiSummarySchema>;

export const clientIntelligenceResponseSchema = z.object({
    rows: z.array(attritionRowSchema),
    kpis: attritionKpiSummarySchema,
});
export type ClientIntelligenceResponse = z.infer<typeof clientIntelligenceResponseSchema>;

import { errorBody } from '../schemas/common';

export const viewClientIntelligenceContract = c.router(
    {
        getAttritionRisk: {
            method: 'GET',
            path: '/view/client-intelligence/attrition-risk',
            responses: { 200: clientIntelligenceResponseSchema, 403: errorBody },
            summary:
                'Derived Attrition Risk projection (per-client bands + factors) plus the derivable KPI summary for View > Client Intelligence.',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
